import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as LinkIcon } from '@material-ui/icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { FunctionComponent, useEffect, useState } from 'react';

import { useAppDispatch } from '../../../hooks';
import { DashboardSelectors, dismissWorkReservation, fetchPendingRows, reserveWork } from '../../../reducers/dashboard';
import * as api from '../../../services/api';
import { getWorkUrl } from '../../../services/utils';
import { CopyButton } from '../../../shared';
import { TaskStatus } from '../../../types/task';
import { WorkType } from '../../../types/work';
import { formatFromNow } from '../../../utils/dateUtils';
import DataTable, { DataTableColumn, FilterType } from './dataTable';
import { useAllRowsForFilter } from './useAllRowsForFilter';
import WorkActionColumn from './workActionColumn';

// load dayjs duration and dependent plugin
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);

const columns: DataTableColumn[] = [
    {
        field: 'tags',
        label: 'Tags',
        minWidth: 100,
        format: (value: []) => value && value.length > 0 ? value.join(', ') : '',
        filterType: FilterType.SELECT
    },
    {
        field: 'task|id',
        label: 'Task Id',
        minWidth: 100
    },
    {
        field: 'task|delegatingUserId',
        label: 'Delegator Id',
        minWidth: 100,
        tooltipField: 'task|delegatingUserName'
    },
    {
        field: 'id',
        label: 'Work Id',
        minWidth: 50,
        suffixComponent: function suffixSpecificLink(value: number) {
            return (
                <CopyButton
                    text={getWorkUrl(value)}
                    copyIcon={<LinkIcon color="primary" />}
                    copyTooltip="Copy direct link to clipboard"
                />
            );
        }
    },
    {
        field: 'workType',
        label: 'Work Type',
        minWidth: 100,
        format: (value: number) => _.startCase(_.toLower(WorkType[value])),
        filterType: FilterType.SELECT
    },
    {
        field: 'task|status',
        label: 'Task Status',
        minWidth: 100,
        format: (value: number) => _.startCase(_.toLower(TaskStatus[value]))
    },
    {
        field: 'task|title',
        label: 'Task Title',
        minWidth: 100
    },
    {
        field: 'skills',
        label: 'Skills',
        minWidth: 100,
        format: (value: []) => value && value.length > 0 ? value.join(', ') : '',
        filterType: FilterType.SELECT
    },    
    {
        field: 'created',
        label: 'Time Since Creation',
        minWidth: 100,
        format: (value: string) => value ? formatFromNow(value) : '-'
    },
    {
        field: 'task|created',
        label: 'Time Since Task Creation',
        minWidth: 100,
        format: (value: string) => value ? formatFromNow(value) : '-'
    },
    {
        field: 'workRecordsCount',
        label: 'Work Record Count',
        minWidth: 100
    },
    {
        field: 'workRecordsTotalDurationSeconds',
        label: 'Work Record Duration Sum',
        minWidth: 100,
        format: (value: number) => value ? dayjs.duration(value, 'seconds').humanize() : '-'
    },
    {
        field: 'workRecordsContributorsViewed',
        label: 'Work Record Contributors Viewed',
        minWidth: 100,
        tooltipHeader: 'Number of contributors who viewed this task'
    },
    {
        field: 'reservedWorkerId',
        label: 'Reserved To Worker',
        minWidth: 50,
        filterable: false,
        submittableComponent: function emptyInputElement(row: any, value: any) {
            return (
                <WorkActionColumn
                    row={row}
                    value={value}
                    onSubmitFunc={reserveWork}
                    onCancelFunc={dismissWorkReservation}
                />
            );
        
        },
        tooltipField: 'reservedWorkerName'
    },
    {
        field: 'prohibitedWorkerIds',
        label: 'Prohibited For Workers',
        minWidth: 50,
        format: (value: []) => value && value.length > 0 ? value.join(', ') : '-',
        tooltipField: 'prohibitedWorkerNames'
    }
];

const useStyles = makeStyles({
    root: {
        width: '100%'
    }
});

type Props = Record<string, never>;

const PendingWorkTab: FunctionComponent<Props> = ({}: Props) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const { pendingWorkRows, totalPendingWorkRows } = DashboardSelectors();
    const allRowsForFilter = useAllRowsForFilter(
        totalPendingWorkRows,
        api.fetchDashboardPendingWork,
        'work'
    );

    const [ currentPage, setCurrentPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(100);

    useEffect(() => {
        dispatch(fetchPendingRows({ page: currentPage + 1, resultsPerPage: rowsPerPage }));
    }, [ dispatch, currentPage, rowsPerPage ]);

    return (
        <Paper className={classes.root}>
            <DataTable
                columns={columns}
                rows={pendingWorkRows}
                totalRows={totalPendingWorkRows}
                currentPage={currentPage}
                currentPageChanged={setCurrentPage}
                rowsPerPage={rowsPerPage}
                rowsPerPageChanged={setRowsPerPage}
                showFilters={true}
                allRows={allRowsForFilter}
            />
        </Paper>
    );
};

export default PendingWorkTab;
